import { useState } from 'react';
import {
  Content,
  Header,
  Link,
  LinkButton,
  Page,
  Progress,
} from '@backstage/core-components';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import { useInsights } from '../catalog/pipeline/useInsights';
import { usePalette } from '../Root/PaletteProvider';
import {
  BookIcon,
  BoxIcon,
  FileTextIcon,
  GlobeIcon,
  MapIcon,
  PlusIcon,
  ScrollIcon,
  SearchIcon,
  SparklesIcon,
  TerminalIcon,
} from './docsIcons';
import {
  changedAgo,
  daysSince,
  placesOf,
  type DocsCategory,
  type DocsHubSnapshot,
  type DocsPage,
} from './snapshot';

/**
 * The docs hub (DX-306; promised in specs/docs-hub.md): the landing page in
 * front of the documentation reader — what the portal's documentation holds,
 * by category, what changed lately, and what has not changed in a long time.
 *
 * Its figures come from one snapshot the github-insights backend collects from
 * the repositories every few hours: every page each TechDocs site builds, and
 * the date of the last commit to its source file. The cards, the search
 * field's page count, both lists and the navigation's Docs count all read that
 * one snapshot, so none of them can disagree with another.
 *
 * A card whose number has no source shows none, and a snapshot not collected
 * yet, or not loaded, says so rather than printing zeros: a zero is a
 * measurement, and nothing has been measured.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

const ATLAS = '/docs/default/component/atlas';

/** The pages most readers start from, in Atlas. */
const START_HERE: { label: string; kind: string; to: string }[] = [
  {
    label: 'Big picture',
    kind: 'architecture',
    to: `${ATLAS}/architecture/big-picture/`,
  },
  {
    label: 'Onboarding',
    kind: 'handbook',
    to: `${ATLAS}/handbook/getting-started/onboarding/`,
  },
  {
    label: 'Tech stack',
    kind: 'architecture',
    to: `${ATLAS}/architecture/tech-stack/`,
  },
  {
    label: 'Write an ADR',
    kind: 'tutorial',
    to: `${ATLAS}/handbook/tutorials/write-adr/`,
  },
];

/**
 * The six cards. `unit` is what the number counts. The decisions card counts
 * the ADR files in Atlas — the status of each waits for the ADR index (DX-310)
 * — and the specs card counts Atlas's specs, while the JSON schemas it names
 * live on schemas.revenexx.com, which the portal does not count.
 */
const CATEGORIES: {
  id: DocsCategory;
  title: string;
  unit: (n: number) => string;
  description: string;
  to: string;
  Icon: () => JSX.Element;
}[] = [
  {
    id: 'architecture',
    title: 'Architecture',
    unit: n => `${n} ${n === 1 ? 'page' : 'pages'}`,
    description: 'Big picture, tech stack, multi-tenancy, domain map',
    to: `${ATLAS}/architecture/`,
    Icon: MapIcon,
  },
  {
    id: 'decisions',
    title: 'Decisions',
    unit: n => `${n} ${n === 1 ? 'ADR' : 'ADRs'}`,
    description: 'Every architecture decision record in Atlas',
    to: `${ATLAS}/adr/`,
    Icon: ScrollIcon,
  },
  {
    id: 'handbook',
    title: 'Handbook',
    unit: n => `${n} ${n === 1 ? 'page' : 'pages'}`,
    description: 'Engineering standards, documentation standard, onboarding',
    to: `${ATLAS}/handbook/`,
    Icon: BookIcon,
  },
  {
    id: 'tutorials',
    title: 'Tutorials & how-tos',
    unit: n => `${n} ${n === 1 ? 'guide' : 'guides'}`,
    description:
      'Deploy to Portainer, manage secrets, use KV, publish an event…',
    to: `${ATLAS}/handbook/tutorials/`,
    Icon: TerminalIcon,
  },
  {
    id: 'serviceDocs',
    title: 'Service docs',
    unit: n => `${n} ${n === 1 ? 'site' : 'sites'}`,
    description: 'TechDocs from every repository, one site per catalog entry',
    to: '/docs/sites',
    Icon: BoxIcon,
  },
  {
    id: 'specs',
    title: 'Specs & contracts',
    unit: n => `${n} ${n === 1 ? 'spec' : 'specs'}`,
    description: 'Specs in Atlas; the JSON schemas live on',
    to: `${ATLAS}/specs/is-8-analyze/buyer-accounts/`,
    Icon: FileTextIcon,
  },
];

type Filter = 'all' | 'decisions' | 'runbooks';
const FILTERS: { id: Filter; label: string; empty: string }[] = [
  { id: 'all', label: 'All', empty: 'No page has a date of change yet.' },
  {
    id: 'decisions',
    label: 'Decisions',
    empty: 'No decision has changed yet.',
  },
  { id: 'runbooks', label: 'Runbooks', empty: 'No runbook has a date yet.' },
];

const useStyles = makeStyles(theme => ({
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: 0,
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    minHeight: 52,
    padding: theme.spacing(1, 2),
    border: '1px solid var(--ui-border)',
    borderRadius: 10,
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text-muted)',
    font: 'inherit',
    fontSize: '1rem',
    textAlign: 'left',
    cursor: 'text',
    '&:hover': { borderColor: 'var(--ui-primary)' },
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
  },
  searchText: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  start: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(-1.5),
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 6,
    padding: theme.spacing(0.5, 1.25),
    border: '1px solid var(--ui-border)',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text)',
    '&:hover': { borderColor: 'var(--ui-primary)', textDecoration: 'none' },
  },
  chipKind: { color: 'var(--ui-text-muted)', fontSize: '0.75rem' },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: theme.spacing(2),
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(2),
    border: '1px solid var(--ui-border)',
    borderRadius: 10,
    background: 'var(--ui-bg-elevated)',
    minWidth: 0,
  },
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  cardIcon: {
    display: 'inline-flex',
    padding: 6,
    borderRadius: 6,
    background: 'var(--ui-bg-muted)',
    color: 'var(--ui-text-muted)',
  },
  cardCount: { fontSize: '0.8125rem', color: 'var(--ui-text-muted)' },
  cardTitle: { fontWeight: 600, fontSize: '1rem', color: 'var(--ui-text)' },
  // A link inside running text is underlined: in the dark appearance the
  // link colour and the muted text are both violet, and colour alone did not
  // tell them apart.
  cardText: {
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
    '& a': { textDecoration: 'underline' },
  },
  lower: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  side: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  panel: {
    border: '1px solid var(--ui-border)',
    borderRadius: 10,
    background: 'var(--ui-bg-elevated)',
    minWidth: 0,
  },
  panelHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid var(--ui-border)',
  },
  panelTitle: {
    margin: 0,
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: 'var(--ui-text)',
  },
  panelAside: {
    marginLeft: 'auto',
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
  },
  filters: { display: 'flex', gap: 2, marginLeft: 'auto' },
  filter: {
    padding: theme.spacing(0.25, 1),
    border: 'none',
    borderRadius: 6,
    background: 'none',
    font: 'inherit',
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
    cursor: 'pointer',
    '&[aria-pressed="true"]': {
      background: 'var(--ui-nav-active-bg)',
      color: 'var(--ui-primary)',
      fontWeight: 600,
    },
    '&:focus-visible': { outline: '2px solid var(--ui-primary)' },
  },
  count: {
    padding: '0 6px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--ui-warn-bg)',
    color: 'var(--ui-warn-text)',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  rows: { listStyle: 'none', margin: 0, padding: 0 },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 2),
    '& + &': { borderTop: '1px solid var(--ui-border)' },
    [theme.breakpoints.down('xs')]: { flexWrap: 'wrap', rowGap: 4 },
  },
  rowMain: { flex: 1, minWidth: 0 },
  rowTitle: {
    display: 'block',
    fontWeight: 500,
    overflowWrap: 'anywhere',
  },
  rowPlace: {
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
  },
  badge: {
    flexShrink: 0,
    padding: '1px 6px',
    border: '1px solid var(--ui-border)',
    borderRadius: 4,
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
  },
  age: {
    flexShrink: 0,
    minWidth: 64,
    textAlign: 'right',
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
  },
  stale: {
    flexShrink: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: 'var(--ui-warn-text)',
  },
  note: {
    padding: theme.spacing(1.25, 2),
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
    borderTop: '1px solid var(--ui-border)',
  },
  message: {
    padding: theme.spacing(2),
    fontSize: '0.875rem',
    color: 'var(--ui-text-muted)',
  },
  publicText: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.875rem',
    color: 'var(--ui-text-muted)',
    '& a': { textDecoration: 'underline' },
  },
  unread: { fontSize: '0.8125rem', color: 'var(--ui-text-muted)' },
}));

/** What the snapshot can say, or why it cannot. */
type State =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'collecting' }
  | { kind: 'ready'; snapshot: DocsHubSnapshot };

const stateOf = (loaded: {
  loading: boolean;
  error?: Error;
  data?: DocsHubSnapshot;
}): State => {
  if (loaded.loading) return { kind: 'loading' };
  if (loaded.error || !loaded.data) return { kind: 'failed' };
  if (loaded.data.generatedAt === null) return { kind: 'collecting' };
  return { kind: 'ready', snapshot: loaded.data };
};

/** What a list says while there is nothing to list, in each state. */
const StateMessage = ({ state }: { state: State }) => {
  const classes = useStyles();
  if (state.kind === 'loading') return <Progress />;
  return (
    <Typography
      className={classes.message}
      role={state.kind === 'failed' ? 'alert' : undefined}
    >
      {state.kind === 'failed'
        ? "Couldn't load the documentation overview from github-insights."
        : 'The first documentation snapshot is still being collected. Check back in a few minutes.'}
    </Typography>
  );
};

const PageRow = ({
  page,
  place,
  aside,
}: {
  page: DocsPage;
  place: string;
  aside: JSX.Element;
}) => {
  const classes = useStyles();
  return (
    <li className={classes.row}>
      <span className={classes.rowMain}>
        <Link to={page.location} className={classes.rowTitle}>
          {page.title}
        </Link>
        <span className={classes.rowPlace}>{place}</span>
      </span>
      {aside}
    </li>
  );
};

const RecentlyChanged = ({ state }: { state: State }) => {
  const classes = useStyles();
  const [filter, setFilter] = useState<Filter>('all');
  const pages = state.kind === 'ready' ? state.snapshot.recent[filter] : [];
  const places = placesOf(pages);
  return (
    <section className={classes.panel} aria-labelledby="docs-recent">
      <div className={classes.panelHead}>
        <h2 id="docs-recent" className={classes.panelTitle}>
          Recently changed
        </h2>
        {state.kind === 'ready' && (
          <div className={classes.filters} role="group" aria-label="Show">
            {FILTERS.map(f => (
              <button
                key={f.id}
                type="button"
                className={classes.filter}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {state.kind !== 'ready' && <StateMessage state={state} />}
      {state.kind === 'ready' && !pages.length && (
        <Typography className={classes.message}>
          {FILTERS.find(f => f.id === filter)!.empty}
        </Typography>
      )}
      {pages.length > 0 && (
        <ul className={classes.rows}>
          {pages.map((page, i) => (
            <PageRow
              key={page.location}
              page={page}
              place={places[i]}
              aside={
                <>
                  {page.type && (
                    <span className={classes.badge}>{page.type}</span>
                  )}
                  <span className={classes.age}>
                    {changedAgo(page.lastChanged)}
                  </span>
                </>
              }
            />
          ))}
        </ul>
      )}
    </section>
  );
};

const NeedsReview = ({ state }: { state: State }) => {
  const classes = useStyles();
  const review = state.kind === 'ready' ? state.snapshot.needsReview : null;
  const threshold =
    state.kind === 'ready' ? state.snapshot.freshness.thresholdDays : 90;
  const reviewPlaces = placesOf(review?.pages ?? []);
  return (
    <section className={classes.panel} aria-labelledby="docs-review">
      <div className={classes.panelHead}>
        <h2 id="docs-review" className={classes.panelTitle}>
          Needs review
        </h2>
        {review && review.count > 0 && (
          <span className={classes.count}>{review.count}</span>
        )}
        <span className={classes.panelAside}>
          last changed &gt; {threshold} d
        </span>
      </div>
      {state.kind !== 'ready' && <StateMessage state={state} />}
      {review && review.count === 0 && (
        <Typography className={classes.message}>
          Every page changed within the last {threshold} days.
        </Typography>
      )}
      {review && review.pages.length > 0 && (
        <ul className={classes.rows}>
          {review.pages.map((page, i) => (
            <PageRow
              key={page.location}
              page={page}
              place={reviewPlaces[i]}
              aside={
                <span
                  className={classes.stale}
                  title={`Last changed ${page.lastChanged.slice(0, 10)}`}
                >
                  {daysSince(page.lastChanged)} d
                </span>
              }
            />
          ))}
        </ul>
      )}
      {state.kind === 'ready' && (
        <Typography className={classes.note}>
          {review && review.count > review.pages.length
            ? `The ${review.pages.length} oldest of ${review.count}. `
            : ''}
          Date: {state.snapshot.freshness.source}. A commit is a change, not a
          review — a typo fix resets it.
        </Typography>
      )}
    </section>
  );
};

const PublicDocs = () => {
  const classes = useStyles();
  return (
    <section className={classes.panel} aria-labelledby="docs-public">
      <div className={classes.panelHead}>
        <GlobeIcon />
        <h2 id="docs-public" className={classes.panelTitle}>
          Public docs live elsewhere
        </h2>
      </div>
      <Typography className={classes.publicText}>
        Partner-facing docs are on{' '}
        <Link to="https://www.revenexx.dev">revenexx.dev</Link>, customer help
        on <Link to="https://help.revenexx.com">help.revenexx.com</Link>. The
        search here does not reach them; each has its own.
      </Typography>
    </section>
  );
};

export const DocsHubPage = () => {
  const classes = useStyles();
  const palette = usePalette();
  const state = stateOf(useInsights<DocsHubSnapshot>('/docs-hub'));
  const snapshot = state.kind === 'ready' ? state.snapshot : undefined;
  const pages = snapshot?.totals.pages;

  return (
    <Page themeId="home">
      <Header
        title="Docs"
        subtitle="One place for architecture, decisions, handbook and every service's TechDocs."
      >
        <Grid item className={classes.actions}>
          <LinkButton
            role="link"
            variant="outlined"
            to="/ask"
            startIcon={<SparklesIcon />}
          >
            Ask the docs
          </LinkButton>
          <LinkButton
            role="link"
            variant="contained"
            color="primary"
            to={START_HERE[3].to}
            startIcon={<PlusIcon />}
          >
            New ADR
          </LinkButton>
        </Grid>
      </Header>
      <Content>
        <div className={classes.stack}>
          <button
            type="button"
            className={classes.search}
            aria-label="Search the documentation"
            onClick={() => palette.open('Docs')}
          >
            <SearchIcon />
            <span className={classes.searchText}>
              {pages !== undefined
                ? `Search ${pages.toLocaleString('en-US')} pages`
                : 'Search the documentation'}{' '}
              — architecture, ADRs, runbooks, service docs…
            </span>
          </button>

          <nav className={classes.start} aria-label="Start here">
            <span>Start here</span>
            {START_HERE.map(item => (
              <Link key={item.to} to={item.to} className={classes.chip}>
                {item.label}
                <span className={classes.chipKind}>{item.kind}</span>
              </Link>
            ))}
          </nav>

          <div className={classes.cards}>
            {CATEGORIES.map(({ id, title, unit, description, to, Icon }) => (
              <section
                key={id}
                className={classes.card}
                aria-labelledby={`docs-card-${id}`}
              >
                <div className={classes.cardTop}>
                  <span className={classes.cardIcon}>
                    <Icon />
                  </span>
                  {snapshot && (
                    <span className={classes.cardCount}>
                      {unit(snapshot.categories[id])}
                    </span>
                  )}
                </div>
                <Link
                  id={`docs-card-${id}`}
                  to={to}
                  className={classes.cardTitle}
                >
                  {title}
                </Link>
                <span className={classes.cardText}>
                  {description}
                  {id === 'specs' && (
                    <>
                      {' '}
                      <Link to="https://schemas.revenexx.com">
                        schemas.revenexx.com
                      </Link>
                    </>
                  )}
                </span>
              </section>
            ))}
          </div>

          {snapshot && snapshot.unreadSites.length > 0 && (
            <Typography className={classes.unread}>
              {snapshot.unreadSites.length === 1
                ? '1 site could not be read from GitHub; its pages are not counted.'
                : `${snapshot.unreadSites.length} sites could not be read from GitHub; their pages are not counted.`}
            </Typography>
          )}

          <div className={classes.lower}>
            <RecentlyChanged state={state} />
            <div className={classes.side}>
              <NeedsReview state={state} />
              <PublicDocs />
            </div>
          </div>
        </div>
      </Content>
    </Page>
  );
};
